// action-version-diff.ts — Logique PURE de comparaison de deux versions d'action
// (traçabilité, juin 2026). Aucune dépendance DOM : testable en isolation
// (Vitest). La page #action/:id/versions consomme ces fonctions pour surligner
// les champs modifiés (accent), les ajouts (vert) / suppressions (rouge), et
// pour ancrer la colonne des notes sur la date de la version consultée.

export interface RaciUser {
  id: number;
  trigram?: string | null;
  name?: string | null;
}

export interface CodeLabel {
  code?: string | null;
  label?: string | null;
}

export interface StructEntry extends CodeLabel {
  escalated?: boolean;
}

export interface StatusEntry {
  label: string;
  color?: string | null;
}

// Forme du snapshot figé renvoyé par le backend (services.action_version).
export interface ActionSnapshot {
  text?: string | null;
  priority?: number | null;
  expected_deliverable?: string | null;
  external_link?: string | null;
  progress?: number | null;
  deadline?: string | null;
  deadline_revised?: string | null;
  is_confidential?: boolean;
  status?: StatusEntry | null;
  raci?: { r: RaciUser[]; a: RaciUser[]; c: RaciUser[]; i: RaciUser[] };
  risks?: CodeLabel[];
  projects?: CodeLabel[];
  committees?: CodeLabel[];
  structures?: StructEntry[];
}

// Champs affichés (toujours visibles) dont on sait détecter le changement.
export type DiffField =
  | 'text'
  | 'priority'
  | 'expected_deliverable'
  | 'external_link'
  | 'deadline'
  | 'deadline_revised'
  | 'status'
  | 'progress'
  | 'raci'
  | 'risks'
  | 'projects'
  | 'committees'
  | 'structures';

export type RaciRole = 'r' | 'a' | 'c' | 'i';

function sortedIds(list: RaciUser[] | undefined): number[] {
  return (list || []).map((u) => u.id).sort((a, b) => a - b);
}

function sortedCodes(list: CodeLabel[] | undefined): string[] {
  return (list || []).map((e) => String(e.code ?? '')).sort();
}

function statusLabel(s: StatusEntry | null | undefined): string | null {
  return s ? s.label : null;
}

// Égalité « métier » d'un champ entre deux snapshots. Comparaisons par
// ensembles (ids RACI, codes des listes) pour ignorer l'ordre, et par libellé
// pour le statut (un renommage côté référentiel n'altère pas une version figée,
// mais deux versions figées avec des libellés différents = changement réel).
function fieldEqual(field: DiffField, a: ActionSnapshot, b: ActionSnapshot): boolean {
  switch (field) {
    case 'text':
      return (a.text ?? '') === (b.text ?? '');
    case 'priority':
      return (a.priority ?? null) === (b.priority ?? null);
    // Snapshots antérieurs à août 2026 : champ absent. `?? ''` évite de
    // marquer « modifié » toute version d'avant la feature.
    case 'expected_deliverable':
      return (a.expected_deliverable ?? '') === (b.expected_deliverable ?? '');
    case 'external_link':
      return (a.external_link ?? '') === (b.external_link ?? '');
    case 'progress':
      return (a.progress ?? 0) === (b.progress ?? 0);
    case 'deadline':
      return (a.deadline ?? null) === (b.deadline ?? null);
    case 'deadline_revised':
      return (a.deadline_revised ?? null) === (b.deadline_revised ?? null);
    case 'status':
      return statusLabel(a.status) === statusLabel(b.status);
    case 'raci':
      return (['r', 'a', 'c', 'i'] as RaciRole[]).every(
        (role) =>
          JSON.stringify(sortedIds(a.raci?.[role])) === JSON.stringify(sortedIds(b.raci?.[role])),
      );
    case 'risks':
      return JSON.stringify(sortedCodes(a.risks)) === JSON.stringify(sortedCodes(b.risks));
    case 'projects':
      return JSON.stringify(sortedCodes(a.projects)) === JSON.stringify(sortedCodes(b.projects));
    case 'committees':
      return (
        JSON.stringify(sortedCodes(a.committees)) === JSON.stringify(sortedCodes(b.committees))
      );
    case 'structures':
      return (
        JSON.stringify(sortedCodes(a.structures)) === JSON.stringify(sortedCodes(b.structures))
      );
  }
}

export const ALL_DIFF_FIELDS: DiffField[] = [
  'text',
  'priority',
  'expected_deliverable',
  'external_link',
  'deadline',
  'deadline_revised',
  'status',
  'progress',
  'raci',
  'risks',
  'projects',
  'committees',
  'structures',
];

// Ensemble des champs qui diffèrent entre `current` (version N) et `previous`
// (version N-1). Si `previous` est null (N est la v1), aucun champ n'est marqué
// modifié (rien à comparer).
export function changedFields(
  current: ActionSnapshot,
  previous: ActionSnapshot | null,
): Set<DiffField> {
  const out = new Set<DiffField>();
  if (!previous) return out;
  for (const f of ALL_DIFF_FIELDS) {
    if (!fieldEqual(f, current, previous)) out.add(f);
  }
  return out;
}

export interface UserListDiff {
  added: RaciUser[];
  removed: RaciUser[];
  kept: RaciUser[];
}

// Diff d'une liste d'utilisateurs (un rôle RACI) entre N et N-1 : ajouts,
// suppressions, inchangés. `previous` null → tout est « kept » (pas de
// surlignage sur la v1).
export function diffUserList(
  current: RaciUser[] | undefined,
  previous: RaciUser[] | undefined | null,
): UserListDiff {
  const cur = current || [];
  if (previous == null) return { added: [], removed: [], kept: cur };
  const prevIds = new Set(previous.map((u) => u.id));
  const curIds = new Set(cur.map((u) => u.id));
  return {
    added: cur.filter((u) => !prevIds.has(u.id)),
    kept: cur.filter((u) => prevIds.has(u.id)),
    removed: previous.filter((u) => !curIds.has(u.id)),
  };
}

export interface CodeListDiff {
  added: CodeLabel[];
  removed: CodeLabel[];
  kept: CodeLabel[];
}

// Diff d'une liste code+libellé (risques, projets, comités, structures) par code.
export function diffCodeList(
  current: CodeLabel[] | undefined,
  previous: CodeLabel[] | undefined | null,
): CodeListDiff {
  const cur = current || [];
  if (previous == null) return { added: [], removed: [], kept: cur };
  const code = (e: CodeLabel): string => String(e.code ?? '');
  const prevCodes = new Set(previous.map(code));
  const curCodes = new Set(cur.map(code));
  return {
    added: cur.filter((e) => !prevCodes.has(code(e))),
    kept: cur.filter((e) => prevCodes.has(code(e))),
    removed: previous.filter((e) => !curCodes.has(code(e))),
  };
}

function toEpoch(iso?: string | null): number {
  if (!iso) return NaN;
  const s = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : iso + 'Z';
  return Date.parse(s);
}

// Index d'ancrage de la colonne des notes pour une version donnée. Les notes
// sont triées de la plus RÉCENTE à la plus ANCIENNE. On renvoie l'index de la
// première note dont la date est <= date de la version (la note « contemporaine »
// ou juste antérieure), pour que la colonne se positionne dessus. Si toutes les
// notes sont plus récentes que la version → dernière position (length). Vide → 0.
export function noteAnchorIndex(
  notesNewestFirst: Array<{ created_at?: string | null }>,
  versionIso?: string | null,
): number {
  const vEpoch = toEpoch(versionIso);
  if (Number.isNaN(vEpoch) || notesNewestFirst.length === 0) return 0;
  for (let i = 0; i < notesNewestFirst.length; i++) {
    const nEpoch = toEpoch(notesNewestFirst[i]?.created_at);
    if (!Number.isNaN(nEpoch) && nEpoch <= vEpoch) return i;
  }
  return notesNewestFirst.length;
}
